/**

 * Agent Presence widget — team, campaign, or individual agent; available/unavailable filter.

 */

import { useEffect, useMemo, useState } from 'react';

import { useUser } from '@clerk/clerk-react';

import AgentTile from '../AgentTile';

import AgentPresenceSingle from '../AgentPresenceSingle';

import { agentBelongsToCampaignTitle, formatCodesAsCampaignNames } from '../../lib/campaignCodeToName';

import { agentMatchesPresenceTarget } from '../../lib/formatAgentStatus';

import {

  filterAgentsByStatus,

  type AgentStatusFilter,

} from '../../lib/agentStatusFilter';

import { useWidgetCatalog } from '../../lib/useWidgetCatalog';

import { usePresenceFeed } from '../../lib/usePresenceFeed';

import type { WidgetFilterConfig } from '../../lib/widgetScope';



export type PresenceFilterType = 'team' | 'campaign' | 'agent';



interface WidgetAgentPresenceProps {

  config?: WidgetFilterConfig;

}



const STATUS_FILTER_OPTIONS: { value: AgentStatusFilter; label: string }[] = [

  { value: 'all', label: 'All' },

  { value: 'available', label: 'Available' },

  { value: 'unavailable', label: 'Unavailable' },

];



function toggleBtnStyle(active: boolean): React.CSSProperties {

  return {

    padding: '0.2rem 0.45rem',

    borderRadius: 4,

    border: '1px solid var(--header-border)',

    background: active ? 'rgba(59,130,246,0.25)' : 'transparent',

    color: active ? 'var(--accent)' : 'var(--text-muted)',

    fontSize: '0.65rem',

    cursor: 'pointer',

    fontWeight: active ? 600 : 400,

  };

}



function resolveFilterType(config?: WidgetFilterConfig): PresenceFilterType {

  const explicit = config?.filter_type as PresenceFilterType | undefined;

  if (explicit === 'agent' || explicit === 'team' || explicit === 'campaign') return explicit;

  if ((config?.ipscape_agent_id ?? '').trim()) return 'agent';

  return 'campaign';

}



function liveBadgeStyle(connected: boolean): React.CSSProperties {

  return {

    fontSize: '0.65rem',

    color: connected ? '#22c55e' : 'var(--text-muted)',

    display: 'flex',

    alignItems: 'center',

    gap: '0.25rem',

    flexShrink: 0,

  };

}



export default function WidgetAgentPresence({ config }: WidgetAgentPresenceProps) {

  const { user } = useUser();

  const { agents, connected, presenceWsUrl } = usePresenceFeed();

  const { catalogIdToTitle } = useWidgetCatalog();

  const currentUserName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();



  const savedStatusFilter = (config?.status_filter as AgentStatusFilter | undefined) ?? 'all';

  const [statusFilter, setStatusFilter] = useState<AgentStatusFilter>(savedStatusFilter);



  useEffect(() => {

    setStatusFilter(savedStatusFilter);

  }, [savedStatusFilter]);



  const filterType = resolveFilterType(config);

  const filterValue = (config?.filter_value ?? '').trim();

  const ipscapeAgentId = (config?.ipscape_agent_id ?? '').trim();

  const campaignTitle =

    filterType === 'campaign' && config?.campaign_id

      ? catalogIdToTitle.get(parseInt(config.campaign_id, 10)) ?? filterValue

      : filterValue;



  const scopedAgents = useMemo(() => {

    let list = agents;

    if (filterType === 'agent' && ipscapeAgentId) {

      const match = list.find((a) => agentMatchesPresenceTarget(a, ipscapeAgentId, filterValue));

      return match ? [match] : [];

    }

    if (filterValue || campaignTitle) {

      if (filterType === 'campaign' && campaignTitle) {

        list = list.filter((a) =>

          agentBelongsToCampaignTitle(a.campaign ?? '', a.campaign_codes, campaignTitle, catalogIdToTitle)

        );

      } else if (filterType === 'team' && filterValue) {

        list = list.filter((a) => a.team === filterValue);

      }

    }

    return list;

  }, [agents, filterType, filterValue, ipscapeAgentId, campaignTitle, catalogIdToTitle]);



  const filtered = useMemo(() => {

    if (filterType === 'agent') return scopedAgents;

    return filterAgentsByStatus(scopedAgents, statusFilter);

  }, [scopedAgents, filterType, statusFilter]);



  const needsConfig = filterType === 'agent' ? !ipscapeAgentId : !filterValue && !config?.campaign_id;

  const showSingleView = !needsConfig && scopedAgents.length === 1;

  const singleAgent = scopedAgents[0];

  const isConfiguredAgent = filterType === 'agent';
  const showCampaigns = config?.show_campaigns === true;

  const avatarFor = (name: string) => (name === currentUserName ? user?.imageUrl ?? undefined : undefined);

  const campaignLabelFor = (agent: (typeof agents)[0]) => {
    if (agent.campaign_codes?.length) {
      return formatCodesAsCampaignNames(agent.campaign_codes, catalogIdToTitle);
    }
    return agent.campaign?.trim() || '';
  };



  return (

    <div

      style={{

        height: '100%',

        display: 'flex',

        flexDirection: 'column',

        overflow: 'hidden',

        padding: showSingleView ? '0.35rem 0.5rem' : '0.5rem 0.75rem',

      }}

    >

      {!showSingleView ? (

        <div

          style={{

            display: 'flex',

            alignItems: 'center',

            justifyContent: 'space-between',

            gap: '0.5rem',

            marginBottom: '0.35rem',

            flexShrink: 0,

            flexWrap: 'wrap',

          }}

        >

          {presenceWsUrl && (

            <div style={liveBadgeStyle(connected)}>

              <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor' }} />

              {connected ? 'Live' : 'Connecting…'}

            </div>

          )}

          {presenceWsUrl && !needsConfig && (

            <div style={{ display: 'flex', gap: '0.2rem', marginLeft: 'auto' }} role="group" aria-label="Filter by status">

              {STATUS_FILTER_OPTIONS.map((opt) => (

                <button

                  key={opt.value}

                  type="button"

                  style={toggleBtnStyle(statusFilter === opt.value)}

                  onClick={() => setStatusFilter(opt.value)}

                  aria-pressed={statusFilter === opt.value}

                >

                  {opt.label}

                </button>

              ))}

            </div>

          )}

        </div>

      ) : null}

      {!presenceWsUrl && (

        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>

          Configure <strong>presence_ws_url</strong> in Settings → Integrations (or set <strong>VITE_PRESENCE_WS_URL</strong> for

          local dev).

        </p>

      )}

      {presenceWsUrl && needsConfig ? (

        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>

          Configure widget: choose a team, campaign, or agent.

        </p>

      ) : null}

      {presenceWsUrl && !needsConfig && showSingleView && singleAgent ? (

        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>

          <div style={{ display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>

            <div style={liveBadgeStyle(connected)}>

              <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor' }} />

              {connected ? 'Live' : 'Connecting…'}

            </div>

          </div>

          <AgentPresenceSingle
            agent={singleAgent}
            avatarUrl={avatarFor(singleAgent.name)}
            campaignDisplay={showCampaigns ? campaignLabelFor(singleAgent) : undefined}
          />

        </div>

      ) : null}

      {presenceWsUrl && !needsConfig && !showSingleView ? (

        <div

          style={{

            flex: 1,

            minHeight: 0,

            overflow: 'auto',

            display: 'grid',

            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',

            gridAutoRows: 'min-content',

            gap: '1rem',

            alignItems: 'start',

            alignContent: 'start',

          }}

        >

          {filtered.map((agent) => (

            <AgentTile

              key={agent.id}

              agent={agent}

              avatarUrl={avatarFor(agent.name)}

              noStretch

            />

          ))}

        </div>

      ) : null}

      {!needsConfig && presenceWsUrl && agents.length === 0 && (

        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Waiting for agent data from the presence service.</p>

      )}

      {!needsConfig && presenceWsUrl && agents.length > 0 && scopedAgents.length === 0 && (

        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>

          {isConfiguredAgent

            ? 'Agent not found in live presence (check they are synced in Settings → Agents).'

            : `No agents in this ${filterType === 'campaign' ? 'campaign' : 'team'}.`}

        </p>

      )}

      {!needsConfig && presenceWsUrl && scopedAgents.length > 0 && !showSingleView && filtered.length === 0 && (

        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>

          {`No ${statusFilter === 'all' ? '' : `${statusFilter} `}agents in this ${filterType === 'campaign' ? 'campaign' : 'team'}.`}

        </p>

      )}

    </div>

  );

}

